import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { LogOut, Briefcase, Star, MapPin, CheckCircle2, Clock, AlertCircle, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AgentDashboard() {
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const agentData = JSON.parse(localStorage.getItem('agent') || '{}');
    const token = localStorage.getItem('token');
    
    if (!token || !agentData.id) {
      navigate('/agent/login');
      return;
    }
    
    setAgent(agentData);
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/agents/my-jobs`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('agent');
    navigate('/agent/login');
    toast.success('Logged out successfully');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="text-green-600" size={20} />;
      case 'in_progress':
        return <Clock className="text-blue-600" size={20} />;
      case 'assigned':
        return <AlertCircle className="text-yellow-600" size={20} />;
      default:
        return <Clock className="text-gray-600" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'assigned':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierBadgeColor = (score) => {
    if (score >= 9.0) return '#FFD700'; // Gold
    if (score >= 8.0) return '#C0C0C0'; // Silver
    return '#CD7F32'; // Bronze
  };

  const getTierLabel = (score) => {
    if (score >= 9.0) return 'Gold';
    if (score >= 8.0) return 'Silver';
    return 'Bronze';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5a738f] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const pendingJobs = jobs.filter(j => j.status === 'assigned' || j.status === 'pending');
  const inProgressJobs = jobs.filter(j => j.status === 'in_progress');
  const completedJobs = jobs.filter(j => j.status === 'completed');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src="/hakicheck-logo.png" alt="HakiCheck" className="h-12" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Field Agent Portal</h1>
                <p className="text-sm text-gray-600">Agent Code: {agent?.agent_code}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome, {agent?.full_name}!
          </h2>
          <p className="text-gray-600">Manage your verification jobs and track your performance</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="text-blue-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-blue-600">{jobs.length}</span>
            </div>
            <p className="text-gray-600 font-medium">Total Jobs</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="text-yellow-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-yellow-600">{pendingJobs.length}</span>
            </div>
            <p className="text-gray-600 font-medium">Pending/Assigned</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="text-green-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-green-600">{agent?.total_jobs_completed || 0}</span>
            </div>
            <p className="text-gray-600 font-medium">Completed</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Star className="text-purple-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-purple-600">{agent?.quality_score?.toFixed(1) || '5.0'}</span>
            </div>
            <p className="text-gray-600 font-medium">Quality Score</p>
          </div>
        </div>

        {/* Agent Profile Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Your Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Status</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                agent?.status === 'active' ? 'bg-green-100 text-green-800' : 
                agent?.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {agent?.status || 'Active'}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Tier</p>
              <span 
                className="inline-block px-3 py-1 rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: getTierBadgeColor(agent?.quality_score || 5.0) }}
              >
                {getTierLabel(agent?.quality_score || 5.0)}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Success Rate</p>
              <p className="text-lg font-semibold text-gray-900">{agent?.success_rate || 100}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Service Regions</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {agent?.regions_covered?.map((region, idx) => (
                  <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                    <MapPin size={12} className="mr-1" />
                    {region}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Jobs Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Your Jobs</h3>
          
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">No jobs assigned yet</p>
              <p className="text-sm text-gray-500">Jobs will appear here once assigned by administrators</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(job.status)}
                      <div>
                        <h4 className="font-semibold text-gray-900">{job.job_type || 'Address Verification'}</h4>
                        <p className="text-sm text-gray-600">Check ID: {job.check_id}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-gray-400 mt-0.5" />
                      <span className="text-gray-700">{job.address}</span>
                    </div>
                    {job.agent_notes && (
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="text-xs text-gray-600 font-medium mb-1">Notes:</p>
                        <p className="text-gray-700">{job.agent_notes}</p>
                      </div>
                    )}
                    {job.scheduled_time && (
                      <p className="text-gray-600">
                        <Clock size={14} className="inline mr-1" />
                        Scheduled: {new Date(job.scheduled_time).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AgentDashboard;
